import React, { useState, useEffect, useCallback } from 'react';
import { getApplicationsForPosting, updateApplicationStatus } from '../../services/applicationService';

// Creator/admin-facing list of applications + referrals for a posting.
// Pass `postingId` (it fetches) OR a pre-fetched `applications` array (hub use).

const STATUS_OPTIONS = ['new', 'submitted', 'reviewed', 'shortlisted', 'declined'];

const statusClasses = (s) => {
  switch (s) {
    case 'reviewed': return 'bg-blue-100 text-blue-800';
    case 'shortlisted': return 'bg-green-100 text-green-800';
    case 'declined': return 'bg-red-100 text-red-800';
    default: return 'bg-yellow-100 text-yellow-800'; // new / submitted
  }
};

const fmtDate = (v) => {
  if (!v) return '';
  const d = v.toDate ? v.toDate() : (v.seconds ? new Date(v.seconds * 1000) : new Date(v));
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString();
};

const ApplicationsReceived = ({ postingId, creatorId, applications: initial = null, collapsible = true }) => {
  const [apps, setApps] = useState(initial || []);
  const [loading, setLoading] = useState(!initial);
  const [open, setOpen] = useState(!collapsible);

  const load = useCallback(async () => {
    if (initial || !postingId) return;
    setLoading(true);
    try {
      setApps(await getApplicationsForPosting(postingId, creatorId));
    } catch (e) {
      console.error('Failed to load applications:', e);
    } finally {
      setLoading(false);
    }
  }, [postingId, creatorId, initial]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (initial) setApps(initial); }, [initial]);

  const setStatus = async (id, status) => {
    setApps((prev) => prev.map((a) => (a.id === id ? { ...a, status } : a)));
    try { await updateApplicationStatus(id, status); } catch (e) { console.error('Status update failed:', e); }
  };

  const count = apps.length;

  return (
    <div className="border border-gray-200 rounded-lg bg-white">
      <button
        type="button"
        onClick={() => collapsible && setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
      >
        <span className="font-semibold text-gray-900">Applications &amp; referrals received ({count})</span>
        {collapsible && (
          <svg className={`h-5 w-5 text-gray-400 transform transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
        )}
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-3">
          {loading ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : count === 0 ? (
            <p className="text-sm text-gray-500">No applications or referrals yet.</p>
          ) : (
            apps.map((a) => {
              const isReferral = a.kind === 'referral';
              const name = isReferral ? a.refereeName : a.applicantName;
              const email = isReferral ? a.refereeEmail : a.applicantEmail;
              return (
                <div key={a.id} className="border border-gray-100 rounded-md p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-medium text-gray-900">
                        {name || 'Unknown'}
                        {isReferral && <span className="ml-2 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">Referral</span>}
                      </p>
                      {email && <p className="text-sm text-gray-500">{email}</p>}
                      <p className="text-xs text-gray-400">{fmtDate(a.createdAt)}</p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded flex-shrink-0 ${statusClasses(a.status)}`}>{a.status || 'new'}</span>
                  </div>

                  {a.coverLetter && <p className="text-sm text-gray-700 mt-2 whitespace-pre-wrap">{a.coverLetter}</p>}
                  {isReferral && a.note && <p className="text-sm text-gray-700 mt-2 whitespace-pre-wrap">{a.note}</p>}
                  {isReferral && a.relationship && <p className="text-xs text-gray-500 mt-1">Relationship: {a.relationship}</p>}

                  <div className="flex items-center gap-3 mt-3">
                    {a.resumeUrl && (
                      <a href={a.resumeUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-apn-blue hover:underline">
                        {a.resumeFileName || 'View résumé'} ↓
                      </a>
                    )}
                    <select
                      value={a.status || 'new'}
                      onChange={(e) => setStatus(a.id, e.target.value)}
                      className="ml-auto text-sm border border-gray-300 rounded px-2 py-1"
                    >
                      {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default ApplicationsReceived;
